import { useTreatment } from '@splitsoftware/splitio-react';
import { useEffect } from 'react';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import { blastDataToLogCannon } from '@/utils/blastDataToLogCannon';

export type FeatureFlags = {
  useGoogleApiForRBS?: boolean;
  enablePopularFeaturedDestinations?: boolean;
};

const useFeatureFlags = (): FeatureFlags => {
  const setFeatureFlags = useFeatureFlagsStore(
    (state) => state.setFeatureFlags,
  );
  const setFeatureFlagsReady = useFeatureFlagsStore(
    (state) => state.setFeatureFlagsReady,
  );
  const featureFlags = useFeatureFlagsStore((state) => state.featureFlags);

  const {
    treatment: useGoogleApiForRBSTreatment,
    isReady,
    hasTimedout,
  } = useTreatment({
    name: 'use-google-api-for-rbs',
  });

  const {
    treatment: enablePopularFeaturedTreatment,
    isReady: isPopularFeaturedReady,
    hasTimedout: hasPopularFeaturedTimedout,
  } = useTreatment({
    name: 'enable-popular-featured-destinations',
  });

  useEffect(() => {
    if (hasTimedout || hasPopularFeaturedTimedout) {
      blastDataToLogCannon({
        message:
          'Split SDK timed out before ready — feature flags defaulting to false',
        level: 'error',
        app: 'travel-scheduler',
      });
      setFeatureFlags({
        useGoogleApiForRBS: false,
        enablePopularFeaturedDestinations: false,
      });
      setFeatureFlagsReady(true);
      return;
    }
    if (!isReady || !isPopularFeaturedReady) return;
    setFeatureFlags({
      useGoogleApiForRBS: useGoogleApiForRBSTreatment === 'on',
      enablePopularFeaturedDestinations:
        enablePopularFeaturedTreatment === 'on',
    });
    setFeatureFlagsReady(true);
  }, [
    isReady,
    isPopularFeaturedReady,
    hasTimedout,
    hasPopularFeaturedTimedout,
    useGoogleApiForRBSTreatment,
    enablePopularFeaturedTreatment,
    setFeatureFlags,
    setFeatureFlagsReady,
  ]);

  return featureFlags;
};

export const useFeatureFlag = (flag: keyof FeatureFlags): boolean =>
  useFeatureFlagsStore((state) => state.featureFlags[flag] || false);

export default useFeatureFlags;
